import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import {
  Box,
  Button,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { CompactDataGrid } from "../../../components/data/CompactDataGrid";
import { StatusChip } from "../../../components/data/StatusChip";
import { TableToolbar } from "../../../components/data/TableToolbar";
import { PageWrapper } from "../../../components/layout/PageWrapper";

const initialRules = [
  {
    id: "rule-010",
    name: "Starter reward",
    minimumSpend: 10,
    maximumSpend: 19.99,
    wifiMinutes: 30,
    validityHours: 24,
    scope: "All shops",
    isActive: true,
  },
  {
    id: "rule-020",
    name: "Standard reward",
    minimumSpend: 20,
    maximumSpend: 29.99,
    wifiMinutes: 60,
    validityHours: 24,
    scope: "All shops",
    isActive: true,
  },
  {
    id: "rule-030",
    name: "Plus reward",
    minimumSpend: 30,
    maximumSpend: 49.99,
    wifiMinutes: 120,
    validityHours: 24,
    scope: "All shops",
    isActive: true,
  },
  {
    id: "rule-050",
    name: "Premium reward",
    minimumSpend: 50,
    maximumSpend: null,
    wifiMinutes: 240,
    validityHours: 24,
    scope: "All shops",
    isActive: true,
  },
];

const emptyRule = {
  name: "",
  minimumSpend: "",
  maximumSpend: "",
  wifiMinutes: "",
  validityHours: "24",
  scope: "All shops",
  isActive: true,
};

function formatReward(minutes) {
  if (minutes < 60) return `${minutes} minutes`;
  if (minutes === 60) return "1 hour";
  if (minutes % 60 === 0) return `${minutes / 60} hours`;
  return `${minutes} minutes`;
}

export function RulesPage() {
  const [rules, setRules] = useState(initialRules);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [form, setForm] = useState(emptyRule);

  const filteredRules = useMemo(() => {
    const term = search.trim().toLowerCase();

    return rules.filter((rule) => {
      const matchesTerm =
        !term ||
        rule.name.toLowerCase().includes(term) ||
        rule.scope.toLowerCase().includes(term);
      const matchesStatus =
        status === "all" ||
        (status === "active" && rule.isActive) ||
        (status === "inactive" && !rule.isActive);

      return matchesTerm && matchesStatus;
    });
  }, [rules, search, status]);

  const openCreate = () => {
    setEditingRule(null);
    setForm(emptyRule);
    setDrawerOpen(true);
  };

  const openEdit = (rule) => {
    setEditingRule(rule);
    setForm({
      name: rule.name,
      minimumSpend: String(rule.minimumSpend),
      maximumSpend: rule.maximumSpend == null ? "" : String(rule.maximumSpend),
      wifiMinutes: String(rule.wifiMinutes),
      validityHours: String(rule.validityHours),
      scope: rule.scope,
      isActive: rule.isActive,
    });
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingRule(null);
    setForm(emptyRule);
  };

  const submitRule = (event) => {
    event.preventDefault();
    const minimumSpend = Number(form.minimumSpend);
    const maximumSpend = form.maximumSpend === "" ? null : Number(form.maximumSpend);
    const wifiMinutes = Number(form.wifiMinutes);
    const validityHours = Number(form.validityHours);

    if (!form.name.trim() || minimumSpend < 0 || wifiMinutes <= 0 || validityHours <= 0) {
      toast.error("Complete the required rule fields.");
      return;
    }

    if (maximumSpend != null && maximumSpend < minimumSpend) {
      toast.error("Maximum spend must be greater than minimum spend.");
      return;
    }

    const nextRule = {
      name: form.name.trim(),
      minimumSpend,
      maximumSpend,
      wifiMinutes,
      validityHours,
      scope: form.scope,
      isActive: form.isActive,
    };

    if (editingRule) {
      setRules((current) =>
        current.map((rule) => (rule.id === editingRule.id ? { ...rule, ...nextRule } : rule)),
      );
      toast.success("Rule updated in the interface prototype.");
    } else {
      setRules((current) => [
        {
          id: window.crypto?.randomUUID?.() || String(Date.now()),
          ...nextRule,
        },
        ...current,
      ]);
      toast.success("Rule added to the interface prototype.");
    }

    closeDrawer();
  };

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Rule",
        minWidth: 190,
        flex: 0.9,
        renderCell: (params) => (
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600 }}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: "spendRange",
        headerName: "Spend range",
        minWidth: 150,
        flex: 0.65,
        renderCell: (params) =>
          params.row.maximumSpend == null
            ? `$${params.row.minimumSpend.toFixed(2)}+`
            : `$${params.row.minimumSpend.toFixed(2)} – $${params.row.maximumSpend.toFixed(2)}`,
      },
      {
        field: "wifiMinutes",
        headerName: "WiFi reward",
        minWidth: 125,
        flex: 0.55,
        renderCell: (params) => formatReward(params.row.wifiMinutes),
      },
      {
        field: "validityHours",
        headerName: "Voucher validity",
        width: 122,
        renderCell: (params) => `${params.row.validityHours} hours`,
      },
      {
        field: "scope",
        headerName: "Applies to",
        minWidth: 125,
        flex: 0.55,
      },
      {
        field: "isActive",
        headerName: "Status",
        width: 92,
        renderCell: (params) => (
          <StatusChip
            label={params.row.isActive ? "Active" : "Inactive"}
            status={params.row.isActive ? "active" : "inactive"}
          />
        ),
      },
      {
        field: "actions",
        headerName: "",
        width: 48,
        sortable: false,
        filterable: false,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => (
          <Tooltip title="Edit rule">
            <IconButton aria-label={`Edit ${params.row.name}`} onClick={() => openEdit(params.row)}>
              <EditOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [rules],
  );

  return (
    <PageWrapper>
      <Paper variant="outlined" sx={{ minWidth: 0, overflow: "hidden" }}>
        <TableToolbar title="Reward rules" count={filteredRules.length}>
          <TextField
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search rules"
            aria-label="Search reward rules"
            sx={{ width: { xs: "100%", sm: 205 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ fontSize: 17, color: "text.secondary" }} />
                  </InputAdornment>
                ),
              },
            }}
          />
          <TextField
            select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            aria-label="Filter rules by status"
            sx={{ width: 112 }}
          >
            <MenuItem value="all">All status</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate}>
            New rule
          </Button>
        </TableToolbar>

        <Divider />
        <CompactDataGrid
          rows={filteredRules}
          columns={columns}
          height="calc(100vh - 113px)"
          sx={{ border: 0 }}
        />
      </Paper>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        slotProps={{
          paper: {
            sx: {
              width: { xs: "100%", sm: 410 },
              maxWidth: "100vw",
              borderLeft: "1px solid",
              borderColor: "divider",
            },
          },
        }}
      >
        <Box component="form" onSubmit={submitRule} sx={{ display: "flex", minHeight: "100%", flexDirection: "column" }}>
          <Box
            sx={{
              display: "flex",
              minHeight: 52,
              px: 1.5,
              borderBottom: "1px solid",
              borderColor: "divider",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography component="h2" sx={{ fontSize: "1rem", fontWeight: 600 }}>
                {editingRule ? "Edit reward rule" : "New reward rule"}
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 0.15, fontSize: "0.75rem" }}>
                Spend threshold and WiFi allocation
              </Typography>
            </Box>
            <IconButton aria-label="Close rule form" onClick={closeDrawer}>
              <CloseRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          <Stack spacing={1.25} sx={{ p: 1.5, flex: 1 }}>
            <TextField
              label="Rule name"
              value={form.name}
              onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
              fullWidth
              autoFocus
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField
                label="Minimum spend"
                type="number"
                value={form.minimumSpend}
                onChange={(event) =>
                  setForm((current) => ({ ...current, minimumSpend: event.target.value }))
                }
                slotProps={{ input: { startAdornment: <InputAdornment position="start">$</InputAdornment> } }}
                fullWidth
              />
              <TextField
                label="Maximum spend"
                type="number"
                value={form.maximumSpend}
                onChange={(event) =>
                  setForm((current) => ({ ...current, maximumSpend: event.target.value }))
                }
                placeholder="No limit"
                slotProps={{ input: { startAdornment: <InputAdornment position="start">$</InputAdornment> } }}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField
                label="WiFi minutes"
                type="number"
                value={form.wifiMinutes}
                onChange={(event) =>
                  setForm((current) => ({ ...current, wifiMinutes: event.target.value }))
                }
                fullWidth
              />
              <TextField
                label="Voucher validity (hours)"
                type="number"
                value={form.validityHours}
                onChange={(event) =>
                  setForm((current) => ({ ...current, validityHours: event.target.value }))
                }
                fullWidth
              />
            </Stack>
            <TextField
              label="Applies to"
              select
              value={form.scope}
              onChange={(event) => setForm((current) => ({ ...current, scope: event.target.value }))}
              fullWidth
            >
              <MenuItem value="All shops">All shops</MenuItem>
              <MenuItem value="Harare shops">Harare shops</MenuItem>
              <MenuItem value="Selected shops">Selected shops</MenuItem>
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={form.isActive}
                  onChange={(_, checked) => setForm((current) => ({ ...current, isActive: checked }))}
                />
              }
              label="Active rule"
              sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: "0.8125rem" } }}
            />
          </Stack>

          <Stack
            direction="row"
            spacing={0.75}
            justifyContent="flex-end"
            sx={{ p: 1.25, borderTop: "1px solid", borderColor: "divider" }}
          >
            <Button variant="outlined" color="secondary" onClick={closeDrawer}>
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              {editingRule ? "Save changes" : "Create rule"}
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </PageWrapper>
  );
}
